import { Box, Button, Typography, Alert, Chip, Divider } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppShell, { SettingsGroup, SettingsGroupLabel } from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { fetchCurrentSubscription } from '../services/api';
import { getApiLanguage } from '../config/apiLanguage';
import { useT } from '../i18n/useT';

type SubRow = Record<string, unknown>;

function asRecord(v: unknown): SubRow | null {
  return v && typeof v === 'object' ? (v as SubRow) : null;
}

function str(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  return String(v);
}

function formatDate(v: unknown, lang: string): string | null {
  const s = str(v);
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  try {
    return d.toLocaleDateString(lang === 'de' ? 'de-CH' : 'en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

function planNameFromSlots(slots: number | null, fallback: string): string {
  if (slots == null) return fallback;
  if (slots <= 1) return 'VISI Single';
  if (slots === 2) return 'VISI Duo';
  return `VISI ${slots}`;
}

function inferSlots(type: SubRow | null): number | null {
  if (!type) return null;
  const num = type.num_slots;
  if (typeof num === 'number') return num;
  if (typeof num === 'string' && num.trim() !== '' && !Number.isNaN(Number(num))) return Number(num);
  const name = str(type.name) || '';
  const first = name.charAt(0);
  if (first && !Number.isNaN(Number(first))) return Number(first);
  if (name.includes('one_')) return 1;
  if (name.includes('two_')) return 2;
  return null;
}

function inferPromoters(type: SubRow | null): number | null {
  if (!type) return null;
  const num = type.num_coders;
  if (typeof num === 'number') return num;
  if (typeof num === 'string' && num.trim() !== '' && !Number.isNaN(Number(num))) return Number(num);
  return null;
}

function inferPriceLabel(type: SubRow | null, lang: string, t: (key: 'subscriptionMonth' | 'subscriptionYear') => string): string | null {
  if (!type) return null;
  const price = type.price;
  const duration = type.duration;
  if (price == null || price === '') return null;
  const durationNum = typeof duration === 'number' ? duration : Number(duration);
  const period = durationNum === 12 ? t('subscriptionYear') : t('subscriptionMonth');
  return `${price} CHF / ${period}`;
}

export default function SubscriptionSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchCurrentSubscription>> | null>(null);

  useEffect(() => {
    if (!user?.token) {
      setLoading(false);
      return;
    }
    fetchCurrentSubscription(user.token)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user?.token]);

  const parsed = useMemo(() => {
    const subscription = asRecord(data?.subscription);
    const type = asRecord(subscription?.type);
    const slots = inferSlots(type);
    const promoters = inferPromoters(type);
    const deletedAt = str(subscription?.deleted_at);
    const hasSub = Boolean(subscription) && data?.message === 'Success';
    const isCanceled = Boolean(deletedAt);
    const lang = getApiLanguage();
    const start =
      formatDate(subscription?.start_date ?? subscription?.created_at, lang) ||
      formatDate(subscription?.starts_at, lang);
    const end =
      formatDate(subscription?.end_date ?? subscription?.expires_at, lang) ||
      formatDate(subscription?.expire_date, lang);
    const product =
      str(type?.name) ||
      str(subscription?.product_id) ||
      str(subscription?.product) ||
      null;
    const priceLabel = inferPriceLabel(type, lang, t);
    const planLabel = hasSub
      ? slots != null && promoters != null
        ? t('subscriptionPlanDetail', { slots, promoters })
        : planNameFromSlots(slots, t('subscription'))
      : t('subscriptionFreePlan');
    const endLabel = hasSub && !isCanceled ? t('subscriptionRenews') : t('subscriptionEnd');
    return {
      hasSub,
      isCanceled,
      slots,
      promoters,
      planLabel,
      priceLabel,
      endLabel,
      start,
      end,
      product,
      deletedAt: formatDate(deletedAt, lang),
    };
  }, [data, t]);

  const statusLabel = !parsed.hasSub
    ? t('subscriptionFree')
    : parsed.isCanceled
      ? t('subscriptionCanceled')
      : t('subscriptionActive');

  const statusColor = !parsed.hasSub
    ? 'default'
    : parsed.isCanceled
      ? 'error'
      : 'success';

  return (
    <AppShell title={t('subscription')} showBackButton maxWidth="sm" showCompanion={false}>
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        {t('subscriptionInfo')}
      </Alert>
      {loading && <Typography color="text.secondary">{t('loading')}</Typography>}
      {err && <Alert severity="error">{err}</Alert>}
      {!loading && !err && data && (
        <>
          <SettingsGroupLabel>{t('subscriptionStatus')}</SettingsGroupLabel>
          <SettingsGroup>
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  color={statusColor as 'default' | 'error' | 'success'}
                  label={statusLabel}
                  sx={{ fontWeight: 600 }}
                />
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: DesignSystem.colors.textPrimary }}>
                  {parsed.planLabel}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 14, color: DesignSystem.colors.textSecondary, mb: 1.5 }}>
                {!parsed.hasSub
                  ? t('subscriptionUpgradeHint')
                  : parsed.slots != null
                    ? t('subscriptionSlots', { n: parsed.slots })
                    : t('subscriptionBenefits')}
              </Typography>
              {(parsed.start || parsed.end || parsed.product || parsed.deletedAt || parsed.priceLabel) && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  {parsed.priceLabel && (
                    <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mb: 0.5 }}>
                      <strong>{t('subscriptionPrice')}:</strong> {parsed.priceLabel}
                    </Typography>
                  )}
                  {parsed.product && (
                    <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mb: 0.5 }}>
                      <strong>{t('subscriptionProduct')}:</strong> {parsed.product}
                    </Typography>
                  )}
                  {parsed.start && (
                    <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mb: 0.5 }}>
                      <strong>{t('subscriptionStart')}:</strong> {parsed.start}
                    </Typography>
                  )}
                  {parsed.end && (
                    <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mb: 0.5 }}>
                      <strong>{parsed.endLabel}:</strong> {parsed.end}
                    </Typography>
                  )}
                  {parsed.deletedAt && (
                    <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mb: 0.5 }}>
                      <strong>{t('subscriptionCanceled')}:</strong> {parsed.deletedAt}
                    </Typography>
                  )}
                </>
              )}
              {!parsed.hasSub && (
                <Typography sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary }}>
                  {data.message === 'No subscription' ? t('subscriptionNoPlan') : data.message || t('none')}
                </Typography>
              )}
            </Box>
          </SettingsGroup>

          {data.businesses && data.businesses.length > 0 && (
            <>
              <SettingsGroupLabel>
                {t('linkedBusinesses')} ({data.businesses.length})
              </SettingsGroupLabel>
              <SettingsGroup>
                <Box sx={{ p: 1 }}>
                  {data.businesses.map((b) => {
                    const row = b as { id?: unknown; name?: unknown; username?: unknown };
                    const id = str(row.id);
                    const name = str(row.name) || str(row.username) || t('none');
                    const uname = str(row.username)?.replace(/^@/, '');
                    return (
                      <Box
                        key={id || name}
                        component="button"
                        type="button"
                        onClick={() => {
                          if (uname) navigate(`/${uname}`);
                          else if (id) navigate(`/visi/edit/${id}`);
                        }}
                        sx={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          border: 0,
                          background: 'transparent',
                          cursor: 'pointer',
                          px: 1.5,
                          py: 1.25,
                          borderRadius: 2,
                          '&:hover': { backgroundColor: DesignSystem.colors.primaryLight },
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{name}</Typography>
                        {uname && (
                          <Typography sx={{ fontSize: 12, color: DesignSystem.colors.textSecondary }}>
                            @{uname}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </SettingsGroup>
            </>
          )}

          {data.codes && data.codes.length > 0 && (
            <>
              <SettingsGroupLabel>
                {t('promoterCodes')} ({data.codes.length})
              </SettingsGroupLabel>
              <SettingsGroup>
                <Box sx={{ p: 2.5 }}>
                  {data.codes.map((c, i) => {
                    const row = c as { code?: unknown; id?: unknown; business_id?: unknown };
                    return (
                      <Typography
                        key={str(row.id) || `${i}`}
                        sx={{ fontSize: 13, color: DesignSystem.colors.textSecondary, mb: 0.5, fontFamily: 'monospace' }}
                      >
                        {str(row.code) || JSON.stringify(c)}
                        {row.business_id != null ? ` · #${String(row.business_id)}` : ''}
                      </Typography>
                    );
                  })}
                </Box>
              </SettingsGroup>
            </>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            <Button
              endIcon={<OpenInNew fontSize="small" />}
              href="https://apps.apple.com/account/subscriptions"
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              sx={{ textTransform: 'none', borderRadius: 999 }}
            >
              {t('manageAppStore')}
            </Button>
            <Button
              endIcon={<OpenInNew fontSize="small" />}
              href="https://play.google.com/store/account/subscriptions"
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              sx={{ textTransform: 'none', borderRadius: 999 }}
            >
              {t('managePlayStore')}
            </Button>
          </Box>
        </>
      )}
    </AppShell>
  );
}
